import { apiErrorMessage } from '@agos/api-client'
import { postCustomerAuthMeTableCode } from '@agos/api-client/customers'
import { T, tt } from '@agos/shared'
import { Store } from 'lucide-react'
import { useEffect, useState } from 'react'

import { groupedCode, leftNote, secondsLeft } from './table-code'

/**
 * *Uplatniť body v reštaurácii* on the *Vernostné body* card - `FRONTEND.md` §9.806, owner O153.
 *
 * The customer asks for a code and shows it to the waiter, who types it in *Zaplatiť*. Nothing is spent here: the
 * points come off only when the waiter applies them, which the card says, because a customer looking at a code
 * otherwise wonders whether opening it cost anything.
 */
export function TableCodeCard() {
  const [issued, setIssued] = useState<{ code: string; expiresAt: string } | null>(null)
  const [now, setNow] = useState(() => new Date())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A second's tick only while a code is on screen, so the countdown is true and an idle profile does nothing.
  useEffect(() => {
    if (issued === null) return undefined
    const timer = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(timer)
  }, [issued])

  const ask = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const answer = await postCustomerAuthMeTableCode()
      setIssued({ code: answer.code, expiresAt: answer.expiresAt })
      setNow(new Date())
    } catch (caught) {
      setError(apiErrorMessage(caught, tt('Kód sa nepodarilo vytvoriť. Skúste to znova.')))
    } finally {
      setBusy(false)
    }
  }

  const seconds = issued === null ? 0 : secondsLeft(issued.expiresAt, now)
  const live = issued !== null && seconds > 0

  return (
    <div className="rounded-xl border border-primary/30 bg-background p-3 space-y-2" data-table-code>
      {live ? (
        <>
          <p className="text-xs text-muted-foreground">
            <T>Kód pre čašníka</T>
          </p>
          <p
            className="text-4xl font-black tracking-[0.2em] tabular-nums text-foreground"
            data-table-code-digits
          >
            {groupedCode(issued.code)}
          </p>
          <p className="text-xs font-semibold text-primary" data-table-code-left>
            {leftNote(seconds)}
          </p>
          <p className="text-[11px] text-muted-foreground" data-table-code-how>
            <T>
              Ukážte tento kód čašníkovi pri platení. Body sa odpočítajú, až keď ich čašník uplatní na účte. Kód platí
              len raz.
            </T>
          </p>
          <button
            type="button"
            disabled={busy}
            className="text-xs font-semibold underline disabled:opacity-50"
            data-table-code-new
            onClick={() => void ask()}
          >
            <T>Nový kód</T>
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            disabled={busy}
            className="w-full min-h-[44px] rounded-xl bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            data-table-code-ask
            onClick={() => void ask()}
          >
            <Store className="w-4 h-4" />
            <T>Uplatniť body v reštaurácii</T>
          </button>
          {issued !== null && (
            <p className="text-[11px] text-amber-500" data-table-code-expired>
              {leftNote(0)}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            <T>V reštaurácii dostanete kód, ktorý ukážete čašníkovi - body sa odpočítajú až pri platbe.</T>
          </p>
        </>
      )}
      {error !== null && (
        <p className="text-[11px] font-semibold text-destructive" role="alert" data-table-code-error>
          {error}
        </p>
      )}
    </div>
  )
}
